import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { CalendarDays, Users } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'company_register.selected_user_ids';
const LS_ROLE_KEY = 'company_register.selected_roles';

const NEXT = { '': 'ON', ON: 'OFF', OFF: 'LEAVE', LEAVE: 'OT', OT: 'SAT', SAT: 'PH', PH: '' };

const CELL_STYLE = {
  '': 'bg-white text-slate-400 hover:bg-slate-50',
  ON: 'bg-emerald-100 text-emerald-800 font-bold hover:bg-emerald-200',
  OFF: 'bg-rose-100 text-rose-800 font-bold hover:bg-rose-200',
  LEAVE: 'bg-violet-100 text-violet-800 font-bold hover:bg-violet-200',
  OT: 'bg-blue-100 text-blue-800 font-bold hover:bg-blue-200',
  SAT: 'bg-orange-100 text-orange-800 font-bold hover:bg-orange-200',
  PH: 'bg-fuchsia-100 text-fuchsia-800 font-bold hover:bg-fuchsia-200',
};

const WEEKEND_LABELS = new Set(['Sat', 'Sun']);

export default function CompanyRegisterPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(today);
  const [days, setDays] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingCell, setSavingCell] = useState('');

  const loadSet = (key) => {
    try { const raw = localStorage.getItem(key); return new Set(raw ? JSON.parse(raw) : []); } catch { return new Set(); }
  };
  const [selectedIds, setSelectedIds] = useState(() => loadSet(LS_KEY));
  const [selectedRoles, setSelectedRoles] = useState(() => loadSet(LS_ROLE_KEY));

  const persist = (key, idSet, setter) => {
    setter(new Set(idSet));
    try { localStorage.setItem(key, JSON.stringify([...idSet])); } catch { /* no-op */ }
  };
  const toggleStaff = (id) => { const s = new Set(selectedIds); s.has(id) ? s.delete(id) : s.add(id); persist(LS_KEY, s, setSelectedIds); };
  const selectAll = () => persist(LS_KEY, new Set(staff.map(s => s.user_id)), setSelectedIds);
  const clearStaff = () => persist(LS_KEY, new Set(), setSelectedIds);
  const toggleRole = (role) => { const s = new Set(selectedRoles); s.has(role) ? s.delete(role) : s.add(role); persist(LS_ROLE_KEY, s, setSelectedRoles); };
  const clearRoles = () => persist(LS_ROLE_KEY, new Set(), setSelectedRoles);

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/company-register`, { headers, params: { month } });
      setDays(res.data?.days || []);
      setStaff(res.data?.staff || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load register');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  const cycleCell = async (userId, day) => {
    const idx = staff.findIndex(r => r.user_id === userId);
    if (idx < 0) return;
    const row = staff[idx];
    const next = NEXT[row.entries[day] || ''];
    const key = `${userId}-${day}`;
    setSavingCell(key);

    const newStaff = staff.slice();
    newStaff[idx] = { ...row, entries: { ...row.entries, [day]: next } };
    setStaff(newStaff);

    try {
      const res = await axios.post(`${API_URL}/company-register/cell`,
        { user_id: userId, month, day, value: next },
        { headers });
      newStaff[idx] = { ...newStaff[idx], totals: res.data?.totals || row.totals };
      setStaff(newStaff);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
      const revert = staff.slice(); revert[idx] = row; setStaff(revert);
    } finally {
      setSavingCell('');
    }
  };

  const allRoles = useMemo(() => {
    const m = new Map();
    for (const s of staff) m.set(s.role, s.role_label || s.role);
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [staff]);

  // Visible staff: filtered by role first (if any selected), then by explicit user pick
  const visibleStaff = useMemo(() => {
    let list = staff;
    if (selectedRoles.size > 0) list = list.filter(s => selectedRoles.has(s.role));
    if (selectedIds.size > 0) list = list.filter(s => selectedIds.has(s.user_id));
    return list;
  }, [staff, selectedIds, selectedRoles]);

  const grandWorked = visibleStaff.reduce((s, r) => s + (r.totals?.worked || 0), 0);
  const grandLeave = visibleStaff.reduce((s, r) => s + (r.totals?.leave || 0), 0);
  const selectionLabel = (selectedIds.size === 0 && selectedRoles.size === 0)
    ? `All staff (${staff.length})`
    : `${visibleStaff.length} of ${staff.length} staff`;

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarDays className="h-6 w-6" /> Company Attendance Register
            </h1>
            <p className="text-sm text-muted-foreground">
              Click a cell to cycle <b className="text-emerald-700">ON</b> → <b className="text-rose-700">OFF</b> → <b className="text-violet-700">LEAVE</b> → <b className="text-blue-700">OT</b> → <b className="text-orange-700">SAT</b> (Saturday) → <b className="text-fuchsia-700">PH</b> (Public Holiday) → blank.
              Worked = ON+OT+SAT+PH. Filter by role or person below.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 gap-2" data-testid="role-picker-btn">
                  <Users className="h-4 w-4" />
                  {selectedRoles.size === 0 ? `All roles (${allRoles.length})` : `${selectedRoles.size} role${selectedRoles.size === 1 ? '' : 's'}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold">Filter by role</span>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={clearRoles} data-testid="role-picker-clear">Clear</Button>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {allRoles.map(([role, label]) => (
                    <label key={role} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-slate-50 rounded px-1">
                      <Checkbox checked={selectedRoles.has(role)} onCheckedChange={() => toggleRole(role)} data-testid={`role-${role}`} />
                      <span>{label}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{staff.filter(s => s.role === role).length}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 gap-2" data-testid="staff-picker-btn">
                  <Users className="h-4 w-4" />
                  {selectionLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="end">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold">Filter by person</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={selectAll} data-testid="staff-picker-all">All</Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={clearStaff} data-testid="staff-picker-clear">Clear</Button>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {staff.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">No staff.</p>
                  ) : staff.map(s => (
                    <label key={s.user_id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-slate-50 rounded px-1">
                      <Checkbox checked={selectedIds.size === 0 || selectedIds.has(s.user_id)} onCheckedChange={() => {
                        if (selectedIds.size === 0) {
                          const all = new Set(staff.map(x => x.user_id)); all.delete(s.user_id); persist(LS_KEY, all, setSelectedIds);
                        } else { toggleStaff(s.user_id); }
                      }} data-testid={`pick-${s.user_id}`} />
                      <span>{s.full_name}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{s.role_label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Clear = show all. Selection saved on this device.</p>
              </PopoverContent>
            </Popover>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
              data-testid="company-register-month"
            />
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
              <span>{month} · {days.length} days · {visibleStaff.length} of {staff.length} staff · Worked-days: <b className="text-emerald-700">{grandWorked}</b> · Leave: <b className="text-violet-700">{grandLeave}</b></span>
              <span className="flex items-center gap-2 text-xs font-normal flex-wrap justify-end">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-200 border border-emerald-400" /> ON</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-rose-200 border border-rose-400" /> OFF</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-violet-200 border border-violet-400" /> LEAVE</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-200 border border-blue-400" /> OT</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-orange-200 border border-orange-400" /> SAT</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-fuchsia-200 border border-fuchsia-400" /> PH</span>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : visibleStaff.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                {staff.length === 0 ? 'No users found.' : 'No staff selected — pick at least one above or click All.'}
              </div>
            ) : (
              <table className="w-full text-xs border-collapse" data-testid="company-register-grid">
                <thead>
                  <tr>
                    <th className="text-left p-1 sticky left-0 bg-white z-10 border-b border-r" style={{ minWidth: 56 }}>Date</th>
                    <th className="text-left p-1 sticky bg-white border-b border-r" style={{ left: 56, minWidth: 50, zIndex: 10 }}>Day</th>
                    {visibleStaff.map(s => (
                      <th key={s.user_id} className="p-1 border-b border-r text-center font-semibold" style={{ minWidth: 80 }}>
                        <div className="truncate">{s.full_name}</div>
                        <div className="text-[9px] font-normal text-slate-500 truncate">{s.role_label}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {days.map(d => {
                    const isWeekend = WEEKEND_LABELS.has(d.label);
                    return (
                      <tr key={d.day} className={isWeekend ? 'bg-slate-50' : ''}>
                        <td className={`p-1 border-b border-r font-mono sticky left-0 z-10 ${isWeekend ? 'bg-slate-50' : 'bg-white'}`}>{parseInt(d.day, 10)}</td>
                        <td className={`p-1 border-b border-r sticky z-10 ${isWeekend ? 'bg-slate-50 text-slate-500' : 'bg-white'}`} style={{ left: 56 }}>{d.label}</td>
                        {visibleStaff.map(s => {
                          const v = s.entries[d.day] || '';
                          const key = `${s.user_id}-${d.day}`;
                          return (
                            <td key={key} className="p-0 border-b border-r text-center">
                              <button
                                type="button"
                                onClick={() => cycleCell(s.user_id, d.day)}
                                disabled={savingCell === key}
                                className={`w-full h-7 text-[10px] tracking-wide transition-colors ${CELL_STYLE[v]} ${savingCell === key ? 'opacity-50' : ''}`}
                                data-testid={`cell-${s.user_id}-${d.day}`}
                                title={`${s.full_name} · ${d.day} ${d.label}`}
                              >
                                {v || '·'}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  <tr className="bg-emerald-50 font-semibold">
                    <td className="p-1 border-t" colSpan={2}>WORKED (ON+OT+SAT+PH)</td>
                    {visibleStaff.map(s => (
                      <td key={`w-${s.user_id}`} className="p-1 border-t text-center text-emerald-800">{s.totals?.worked || 0}</td>
                    ))}
                  </tr>
                  <tr className="text-[10px] text-muted-foreground">
                    <td className="p-1" colSpan={2}>ON · OFF · LEAVE · OT · SAT · PH</td>
                    {visibleStaff.map(s => (
                      <td key={`d-${s.user_id}`} className="p-1 text-center">
                        <span className="text-emerald-700">{s.totals?.on || 0}</span>
                        {' · '}<span className="text-rose-700">{s.totals?.off || 0}</span>
                        {' · '}<span className="text-violet-700">{s.totals?.leave || 0}</span>
                        {' · '}<span className="text-blue-700">{s.totals?.ot || 0}</span>
                        {' · '}<span className="text-orange-700">{s.totals?.sat || 0}</span>
                        {' · '}<span className="text-fuchsia-700">{s.totals?.ph || 0}</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
